import { useId, type ReactNode } from "react";

import { Avatar } from "./Avatar.js";
import { AvatarGroup, type AvatarGroupPerson } from "./AvatarGroup.js";

import { cn } from "../lib/cn.js";

/**
 * The row of figures a page leads with, and one card in it.
 *
 * ==========================================================================================
 * WHAT A STAT CARD IS FOR, AND WHAT IT MUST NOT BECOME
 * ==========================================================================================
 *
 * A figure the reader wants before they want anything else: what this comes to per month, what it
 * was last month, who owes whom. It is the answer, not a summary of the answer — everything in a
 * stat card must be a number or a name, because the moment one starts holding a sentence the row
 * stops being scannable and becomes a paragraph laid out in boxes.
 *
 * TWO SHAPES, ONE COMPONENT. `value` is the single-figure card. `rows` is the decomposition card —
 * a member and their share, a pair and their transfer. A card may carry both, and the ordering is
 * fixed here rather than left to the caller so that four cards in a row cannot each choose a
 * different vertical rhythm.
 *
 * ==========================================================================================
 * NO SHEEN, DELIBERATELY
 * ==========================================================================================
 *
 * `SheenSurface` was written for the payment cards, where the point is that a card is an OBJECT
 * with a surface. A stat tile is a panel, and giving it the same finish would spend the treatment
 * that distinguishes the one page where it means something. So this is the ordinary panel every
 * other section on every page uses, and the row reads as part of the page rather than as a set of
 * things sitting on it.
 *
 * ==========================================================================================
 * THE VALUE IS NOT `tabular-nums`, AND THE ROWS ARE
 * ==========================================================================================
 *
 * `tabular-nums` gives every digit the width of a zero, which is what makes a COLUMN of figures
 * line up — and what makes a single large number look gappy, because a `1` is then as wide as an
 * `8`. So the headline figure uses the font's proportional figures and the rows, which are a column
 * that must align, use tabular ones. The two are not a preference either way round: each is wrong
 * in the other's place.
 */

export interface StatCardRow {
  /** Stable across renders — a member id, a card id, a pair key. Not the index. */
  key: string;
  label: string;
  value: string;
  /**
   * Recessive, for a row that is present but not the household's business any more — a share
   * carried forward for somebody who has left, an account whose owner is gone.
   */
  muted?: boolean;
  /**
   * The picture of the person this row is about (020), when it is about one.
   *
   * ABSENT MEANS NO MARK, exactly as on `Avatar`, and it is absent in three different situations
   * that must not be conflated: the row is about the household rather than a person ("Joint"), the
   * row is about a person who has no picture, or the row is about a pair. The first gets nothing,
   * the second gets initials, and the third is why `avatarName` exists separately from `label` —
   * a settle-up row reads "Alice owes Bob", which is not one person's name.
   */
  avatarUrl?: string | null | undefined;
  /**
   * Whose face to draw, when `label` is not simply their name.
   *
   * Falls back to `label`. Pass it explicitly for a row whose label is a sentence or a pair, and
   * omit it entirely — along with `avatarUrl` — for a row that is about nobody.
   */
  avatarName?: string;
  /**
   * Everybody, for a row that is about the household rather than one member.
   *
   * The "Who holds it" and "How it is earned" cards each carry a joint row beside the per-member
   * ones. Without this it was the only row in its card with no mark at all, so its label started a
   * few pixels left of every other — the same misalignment the tables had, one component along.
   */
  avatarGroup?: readonly AvatarGroupPerson[];
}

/**
 * Which way a change went, and it is a GLYPH plus a sign rather than a colour.
 *
 * Colour alone cannot carry it: red/green is the commonest confusion there is, and GLADIS has no
 * status palette to draw a correct pair from — the design tokens define page, panel, ink, muted,
 * line and one violet accent, and inventing a red here would be a design-system decision made in
 * the corner of a card. The arrow and the signed figure say it in text, which is also what a screen
 * reader reads out.
 *
 * `level` is not "no change to report" — it is a change of exactly zero, which is a finding. A card
 * with nothing to compare against omits the trend entirely instead.
 */
export type StatTrend = "up" | "down" | "level";

const TREND_GLYPH: Record<StatTrend, string> = { up: "▲", down: "▼", level: "→" };

export interface StatCardProps {
  /** The eyebrow. Sentence case, no trailing colon, and it is the card's accessible name. */
  label: string;
  /** The headline figure, already formatted. Omitted on a card that is only a decomposition. */
  value?: string;
  /**
   * Which way `value` moved. Rendered before the figure.
   *
   * WHETHER UP IS GOOD IS NOT THIS COMPONENT'S BUSINESS. Savings rising and expenses rising are the
   * same arrow and opposite news, and a component that decided which was which would have to be
   * told what kind of money it is holding.
   */
  trend?: StatTrend;
  /** One short line under the figure: what it is measured against, or what it is made of. */
  hint?: ReactNode;
  /**
   * Controls that change what the card shows, at the end of its heading row.
   *
   * THE SAME SHAPE `Page.Title`'s `actions` SLOT HAS, one level down, and it exists for one card: the
   * dashboard's health card is the only piece on that page with filters, and `dashboard-layout.md`
   * requires them to "sit on the card itself" — a control floating above the panel would read as
   * belonging to the page and would suggest it filtered the cards beside it, which it must not.
   *
   * Not a select, not a list of options: a `ReactNode`, because a primitive that knew what a filter was
   * would be deciding a page's vocabulary from inside the design system.
   */
  controls?: ReactNode;
  rows?: readonly StatCardRow[];
  /** For the e2e suite, on the card. A name-based locator would match the same figure elsewhere. */
  testId?: string;
  /**
   * For the e2e suite, on the FIGURE ALONE.
   *
   * Both exist because the two kinds of assertion want different elements: `toContainText` wants the card, and
   * `toHaveText("€ 6.834,56")` wants an element whose entire text is the figure — which the card is not, since
   * it also holds a label and a hint.
   */
  valueTestId?: string;
  className?: string;
}

export function StatCard({
  label,
  value,
  trend,
  hint,
  controls,
  rows,
  testId,
  valueTestId,
  className,
}: StatCardProps): ReactNode {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      {...(testId === undefined ? {} : { "data-testid": testId })}
      className={cn("rounded-control border border-line bg-panel p-5", className)}
    >
      {/*
        A HEADING, NOT A STYLED PARAGRAPH. It looks like an eyebrow — small, spaced, muted — and it
        is structurally the card's heading, so the row appears in a screen reader's outline as four
        named things rather than as a wall of numbers. `h2`, because these are peers of the page's
        other sections and the page's own title is the `h1`.
      */}
      {/*
        The heading and its controls share a row, and the heading keeps its own element so a screen
        reader's outline is unchanged whether or not a card carries controls.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={headingId} className="text-xs font-medium tracking-wide text-muted uppercase">
          {label}
        </h2>
        {controls === undefined ? null : <div className="flex items-center gap-2">{controls}</div>}
      </div>

      {value === undefined ? null : (
        <p className="mt-2 flex items-baseline gap-2 text-2xl font-semibold text-ink">
          {trend === undefined ? null : (
            // Not `aria-hidden`: the arrow is the direction, and hiding it would leave a screen
            // reader with a bare "+ € 45,00" whose sign is the only clue.
            <span className="text-base leading-none">{TREND_GLYPH[trend]}</span>
          )}
          {/*
            `valueTestId` GOES ON THE FIGURE, not on the paragraph that holds it — which is what this
            prop's own documentation says and what it stopped being the moment a card carried a trend.
            With the glyph inside the same element, `toHaveText("€ 5.600,00")` reads "→€ 5.600,00" and
            fails on a figure that is perfectly correct. Every existing caller is unaffected: with no
            trend the paragraph's text and the figure's were already the same string.
          */}
          <span {...(valueTestId === undefined ? {} : { "data-testid": valueTestId })}>
            {value}
          </span>
        </p>
      )}

      {rows === undefined || rows.length === 0 ? null : (
        <dl className={cn("flex flex-col gap-1", value === undefined ? "mt-2" : "mt-3")}>
          {rows.map((row) => (
            <div key={row.key} className="flex items-baseline justify-between gap-3">
              {/*
                THE ROW'S VERTICAL RHYTHM DOES NOT MOVE. The avatar is `sm` and sits inside the
                `<dt>` rather than beside it, so a card whose rows have faces is exactly as tall as
                one whose rows do not — four cards in a row must not each choose a different height
                depending on who has uploaded a photograph.
              */}
              <dt
                className={cn(
                  "flex items-center gap-2 text-sm",
                  row.muted ? "text-muted italic" : "text-ink",
                )}
              >
                {row.avatarGroup !== undefined && row.avatarGroup.length > 0 ? (
                  <AvatarGroup people={row.avatarGroup} size="sm" />
                ) : row.avatarName === undefined && row.avatarUrl === undefined ? null : (
                  <Avatar
                    src={row.avatarUrl ?? null}
                    name={row.avatarName ?? row.label}
                    size="sm"
                  />
                )}
                {row.label}
              </dt>
              <dd
                className={cn(
                  "text-sm font-medium tabular-nums",
                  row.muted ? "text-muted" : "text-ink",
                )}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {hint === undefined ? null : <p className="mt-3 text-xs text-muted">{hint}</p>}
    </section>
  );
}

/**
 * The row the cards sit in.
 *
 * `auto-fit` WITH A MINIMUM RATHER THAN A FIXED COLUMN COUNT, because the number of cards is data:
 * the settle-up card is absent when there is nothing to settle and the change card is absent until
 * there is a month to compare against. A `lg:grid-cols-4` would leave a hole where those were; this
 * lets three cards fill the width they have.
 *
 * The minimum is what stops it: below 14rem a card's figure wraps mid-number, so the track breaks to
 * a new line instead.
 */
export function StatRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <div className={cn("grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-4", className)}>
      {children}
    </div>
  );
}
